import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getConversations, setCurrentConversation } from '../store/slices/chat';
import { useAppDispatch, useAppSelector } from '../store/store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChatContext } from './ChatContext';

export const P2PConnectionContext = createContext<any>(null);

export default function P2PConnectionContextProvider({ children }: React.PropsWithChildren) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  let action = searchParams.get('action');
  let conversationId = searchParams.get('id');

  const { socket } = useContext(ChatContext);
  const { allConversations, currentConversation } = useAppSelector<any>((state) => state.chat);

  const [localVideo, setLocalVideo] = useState<boolean>(true);
  const [localAudio, setLocalAudio] = useState<boolean>(true);
  const localMedia = useRef<any>();
  const remoteMedia = useRef<any>();

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [peerConnection, setPeerConnection] = useState<any>(null);
  const [connectionState, setConnectionState] = useState<any>('');
  const [externalIdentifier, setExternalIdentifier] = useState(null);

  // THIS USEEFFECT IS USED FOR GETTING THE CURRENT CONVERSATION
  useEffect(() => {
    if (allConversations.length !== 0) {
      dispatch(
        setCurrentConversation(
          allConversations.find(
            (el: { conversation_id: string | undefined }) => el.conversation_id === conversationId
          )
        )
      );
    } else {
      dispatch(getConversations({ getFirstConversation: false, type: '' }));
    }
  }, [allConversations, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIceCandidate = (e: any) => {
    if (e.candidate) {
      socket.emit(action === 'call' ? 'candidate_offer' : 'candidate_answer', {
        conversation_id: currentConversation?.conversation_id,
        candidate: e.candidate,
      });
    }
  };

  const handleMic = () => {
    let audioTrack: any = stream?.getTracks().find((track: any) => track.kind === 'audio');
    if (audioTrack.enabled) {
      setLocalAudio(false);
      audioTrack.enabled = false;
    } else {
      setLocalAudio(true);
      audioTrack.enabled = true;
    }
  };

  const handleCam = () => {
    let videoTrack: any = stream?.getTracks().find((track: any) => track.kind === 'video');
    if (videoTrack.enabled) {
      setLocalVideo(false);
      videoTrack.enabled = false;
    } else {
      setLocalVideo(true);
      videoTrack.enabled = true;
    }
  };

  const closeTheCall = (id: string) => {
    peerConnection?.close();
    stream?.getTracks().forEach(function (track: any) {
      track.stop();
    });
    const new_message_external_identifier = Number(new Date()).toString(36);
    if (action === 'call') {
      socket.emit('close_sdp_offer', {
        conversation_id: conversationId,
        message_external_identifier: id,
        new_message_external_identifier: new_message_external_identifier,
        person_first_name: '',
        person_last_name: '',
      });
    }
    navigate('/web/chat');
  };

  return (
    <P2PConnectionContext.Provider
      value={{
        localMedia: localMedia,
        remoteMedia: remoteMedia,
        localVideo: localVideo,
        localAudio: localAudio,
        socket,
        stream,
        peerConnection,
        connectionState,
        externalIdentifier,
        setExternalIdentifier: setExternalIdentifier,
        setConnectionState: setConnectionState,
        setPeerConnection: setPeerConnection,
        setLocalAudio: setLocalAudio,
        setLocalVideo: setLocalVideo,
        setStream: setStream,
        handleCam: handleCam,
        handleMic: handleMic,
        closeTheCall: closeTheCall,
        handleIceCandidate: handleIceCandidate,
      }}
    >
      {children}
    </P2PConnectionContext.Provider>
  );
}
